import { memo } from 'react';
import classNames from 'classnames';

import { Slider } from '../../../Slider/Slider';
import { useComponentMixed } from '../../../utils/useComponentMixed';
import { isSvpCategory } from '../../../../modules/helpers';
import { AssetItem } from '../../common/AssetItem/AssetItem';
import { CategoryItem } from '../../common/CategoryItem/CategoryItem';
import { useAssetItemTracking } from '../../common/useAssetItemTracking';
import { useCategoryItemTracking } from '../../common/useCategoryItemTracking';
import { useViewportType } from '../../../utils/useViewportType';

import type { MixedSliderComponent } from '@vgtv/api-client/lib/component/slider';
import type { FC } from 'react';

import * as styles from './MixedSlider.module.scss';

type Props = {
  componentConfig: MixedSliderComponent;
  pageComponentIndex: number;
};

const MixedSliderBase: FC<Props> = ({
  componentConfig,
  pageComponentIndex,
}) => {
  const { isDesktop } = useViewportType();

  const { id, header } = componentConfig;
  const { useLargeThumbnails, skipMetadata } = componentConfig.options;
  const displayLargeThumbnails = useLargeThumbnails && isDesktop;

  const mixed = useComponentMixed(id);

  const { trackAssetClick } = useAssetItemTracking({
    elementType: 'MixedSlider',
    header: componentConfig.header,
    pageComponentIndex,
  });

  const { trackCategoryClick } = useCategoryItemTracking({
    elementType: 'MixedSlider',
    header: componentConfig.header,
    pageComponentIndex,
  });

  return (
    <Slider
      header={header}
      className={classNames(styles.slider, {
        [styles.large]: displayLargeThumbnails,
      })}
    >
      {mixed.map((entity, index) => {
        if (isSvpCategory(entity)) {
          return (
            <CategoryItem
              key={`category:${entity.id}`}
              category={entity}
              componentType={componentConfig.type}
              onClick={() => trackCategoryClick(entity, index)}
              skipMetadata={skipMetadata}
              size={displayLargeThumbnails ? 'large' : 'medium'}
            />
          );
        }

        return (
          <AssetItem
            key={`asset:${entity.id}`}
            asset={entity}
            componentConfig={componentConfig}
            onClick={() => trackAssetClick(entity, index)}
            skipMetadata={skipMetadata}
            size={displayLargeThumbnails ? 'large' : 'medium'}
          />
        );
      })}
    </Slider>
  );
};

export const MixedSlider = memo(MixedSliderBase);
